import { gettext } from '@eventbrite/i18n';
import { getLayer } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import React, { useEffect, useState } from 'react';
import { LISTING_PAGE_REPEATING_CHILD_EVENTS_EXPERIMENT } from '../../../constants';
import { EventMapScopedProps } from '../../../types';
import type { Event } from '../../../types/index';
import { DEFAULT_ZOOM } from '../../_shared/constants';
import { DetailGrid } from '../../_shared/Detail/Detail';
import {
    EventDetailsMainInner,
    EventDetailsSection,
    EventDetailsSectionTitle,
} from '../EventDetails';
import type { DateAndTimeProps } from './DateAndTime/DateAndTime';
import { DateAndTime } from './DateAndTime/DateAndTime';
import type { LocationProps } from './Location/Location';
import { Location } from './Location/Location';

type WhenAndWhereProps = {
    dates: Omit<DateAndTimeProps, 'isRepeatingChildEventsSliderExperiment'>;
    location: LocationProps;
    map: Omit<EventMapScopedProps, 'venueName' | 'venueAddress'>;
    enableRepeatingEventsChildExperiment?: boolean;
} & Pick<Event, 'isSeriesParent'>;

enum RepeatingEventsExperimentValues {
    active = 'active',
    inactive = 'inactive',
}

export const WhenAndWhere = ({
    dates,
    location,
    map,
    isSeriesParent,
    enableRepeatingEventsChildExperiment,
}: WhenAndWhereProps) => {
    const { shouldDisplayMap, location: mapLocation, gMapsApiKey } = map;
    const { shouldDisplayCrawlableSeriesChildrenLinks } = dates;
    const isRepeatingEvent = dates.event.childEvents;
    const isChildEvent = isRepeatingEvent && !isSeriesParent;

    const [isMapExpanded, setIsMapExpanded] = useState(false);
    const [repeatingEventsExperimentValue, setRepeatingEventsExperimentValue] =
        useState<
            | RepeatingEventsExperimentValues.active
            | RepeatingEventsExperimentValues.inactive
            | null
        >(null);
    const WhenAndWhereComponents = () => (
        <>
            {isSeriesParent &&
            !shouldDisplayCrawlableSeriesChildrenLinks ? null : (
                <DateAndTime
                    {...{
                        ...dates,
                        isRepeatingChildEventsSliderExperiment:
                            repeatingEventsExperimentValue ===
                            RepeatingEventsExperimentValues.active,
                    }}
                />
            )}
            <Location
                {...{
                    ...location,
                    onToggleButtonMap: setIsMapExpanded,
                    isMapExpanded: isMapExpanded,
                }}
            />
        </>
    );

    let lazyMap = null;

    if (isMapExpanded) {
        const Map = loadable(() => import('./Map/Map').then(({ Map }) => Map));
        lazyMap = (
            <Map
                location={mapLocation}
                gMapsApiKey={gMapsApiKey}
                mapZoom={DEFAULT_ZOOM}
            />
        );
    }

    useEffect(() => {
        if (
            enableRepeatingEventsChildExperiment &&
            isChildEvent &&
            !repeatingEventsExperimentValue
        ) {
            setRepeatingEventsExperimentValue(
                getLayer(
                    LISTING_PAGE_REPEATING_CHILD_EVENTS_EXPERIMENT.layer,
                    LISTING_PAGE_REPEATING_CHILD_EVENTS_EXPERIMENT.param,
                    LISTING_PAGE_REPEATING_CHILD_EVENTS_EXPERIMENT.defaultValue,
                ),
            );
        }
    }, [
        isChildEvent,
        enableRepeatingEventsChildExperiment,
        repeatingEventsExperimentValue,
    ]);

    return (
        <EventDetailsSection ariaLabelledBy="when-and-where">
            <EventDetailsMainInner>
                <EventDetailsSectionTitle>
                    <h2>{gettext('When and where')}</h2>
                </EventDetailsSectionTitle>
            </EventDetailsMainInner>

            {(!isChildEvent || !enableRepeatingEventsChildExperiment) && (
                <DetailGrid>
                    <WhenAndWhereComponents />
                </DetailGrid>
            )}

            {isChildEvent &&
                repeatingEventsExperimentValue &&
                (repeatingEventsExperimentValue === 'active' ? (
                    <WhenAndWhereComponents />
                ) : (
                    <DetailGrid>
                        <WhenAndWhereComponents />
                    </DetailGrid>
                ))}
            {shouldDisplayMap && lazyMap}
        </EventDetailsSection>
    );
};
